import { useEffect, useRef } from 'react';

const features = [
  {
    icon: 'ri-lock-password-line',
    title: 'End-to-End Encryption',
    description:
      'Every connection is secured with AES-256 encryption, ensuring your data remains private from your device to the destination server.',
  },
  {
    icon: 'ri-eye-off-line',
    title: 'Zero-Log Policy',
    description:
      'We never store, track, or share your activity. What you do online stays entirely your own — always.',
  },
  {
    icon: 'ri-rocket-line',
    title: 'Lightning-Fast Performance',
    description:
      'Optimized for iOS, our apps run lean and fast — no background drain, no slowdowns, just seamless operation.',
  },
  {
    icon: 'ri-hard-drive-3-line',
    title: 'Smart Storage Cleanup',
    description:
      'Advanced scanning algorithms identify and eliminate redundant files, duplicate media, and stale caches in seconds.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Privacy Protection',
    description:
      'Proactive monitoring tools alert you to threats before they become problems, keeping you one step ahead at all times.',
  },
  {
    icon: 'ri-smartphone-line',
    title: 'Native iOS Design',
    description:
      'Built with Apple\'s Human Interface Guidelines at heart — familiar, intuitive, and perfectly integrated with iOS.',
  },
];

export default function FeaturesSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll<HTMLElement>('[data-feat]').forEach((el, i) => {
              setTimeout(() => {
                el.style.opacity = '1';
                el.style.transform = 'translateY(0)';
              }, i * 80);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="features" ref={sectionRef} className="py-28 md:py-36 bg-gray-900">
      <div className="max-w-7xl mx-auto px-6 md:px-10">

        {/* Heading */}
        <div className="text-center mb-16 md:mb-24 max-w-2xl mx-auto">
          <p className="text-xs font-semibold tracking-widest uppercase text-emerald-500 mb-5">
            Features
          </p>
          <h2 className="text-4xl md:text-5xl font-thin text-white leading-tight">
            Built for{' '}
            <strong className="font-semibold">Performance &amp; Privacy</strong>
          </h2>
          <p className="mt-6 text-gray-400 text-base leading-relaxed">
            Every feature is purposefully crafted to make your iOS experience
            safer, faster, and more enjoyable.
          </p>
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {features.map((feat, idx) => (
            <div
              key={feat.title}
              data-feat
              className="bg-white/5 border border-white/8 rounded-2xl p-7 md:p-8 hover:bg-white/8 hover:border-emerald-500/30 transition-all duration-500"
              style={{ opacity: 0, transform: 'translateY(28px)', transitionDelay: `${idx * 40}ms` }}
            >
              <div className="w-10 h-10 flex items-center justify-center mb-5 rounded-xl bg-emerald-500/10">
                <i className={`${feat.icon} text-emerald-400 text-xl`} />
              </div>
              <h3 className="text-base font-semibold text-white mb-3">{feat.title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed">{feat.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
